import React, { useState, useEffect, useRef } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  height?: number;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write something...',
  className = '',
  height = 200
}: RichTextEditorProps) {
  // Track if component is mounted
  const [mounted, setMounted] = useState(false);
  // Track if editor is in HTML mode
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  // Reference to the editor element
  const editorRef = useRef<HTMLDivElement>(null);
  // Track if edits should be processed
  const shouldProcessEditRef = useRef(true);
  // Store the last known selection
  const lastSelectionRef = useRef<{ start: number, end: number, node: Node | null }>({
    start: 0,
    end: 0,
    node: null
  });

  // First useEffect - handle mounting
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Second useEffect - sync content from props
  // This must be before any conditional returns to avoid hook ordering issues
  useEffect(() => {
    if (!editorRef.current || !shouldProcessEditRef.current) {
      shouldProcessEditRef.current = true;
      return;
    }

    // Only update if the editor doesn't have focus
    if (document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Save selection before any content changes
  const saveSelection = () => {
    if (!window.getSelection || !editorRef.current) return null;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(editorRef.current);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const start = preSelectionRange.toString().length;

    lastSelectionRef.current = {
      start,
      end: start + range.toString().length,
      node: range.commonAncestorContainer
    };

    return range.cloneRange();
  };

  // Restore selection after content changes
  const restoreSelection = () => {
    if (!window.getSelection || !editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    if (selection.rangeCount > 0) selection.removeAllRanges();

    // If we don't have a saved position, don't attempt to restore
    if (lastSelectionRef.current.node === null) return;

    let charIndex = 0;
    const range = document.createRange();
    range.setStart(editorRef.current, 0);
    range.collapse(true);

    const nodeStack = [editorRef.current];
    let foundStart = false;
    let foundEnd = false;
    let node, nextCharIndex;

    while (!foundEnd && nodeStack.length > 0) {
      node = nodeStack.pop();
      if (!node) continue;

      // Skip comment nodes
      if (node.nodeType === 8) continue;

      // Text node
      if (node.nodeType === 3) {
        nextCharIndex = charIndex + node.textContent!.length;
        
        // Check if this node contains the selection start
        if (!foundStart && lastSelectionRef.current.start >= charIndex && lastSelectionRef.current.start <= nextCharIndex) {
          range.setStart(node, lastSelectionRef.current.start - charIndex);
          foundStart = true;
        }
        
        // Check if this node contains the selection end
        if (foundStart && !foundEnd && lastSelectionRef.current.end >= charIndex && lastSelectionRef.current.end <= nextCharIndex) {
          range.setEnd(node, lastSelectionRef.current.end - charIndex);
          foundEnd = true;
        }
        
        charIndex = nextCharIndex;
      } 
      // Element node
      else {
        // Traverse child nodes in reverse order (to handle the stack correctly)
        let i = node.childNodes.length;
        while (i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }

    if (foundStart) {
      selection.addRange(range);
    }
  };

  // Function to safely handle paragraph insertion
  const insertParagraph = () => {
    if (!editorRef.current) return;
    
    // Save selection
    const savedSelection = saveSelection();
    
    // Insert paragraph
    document.execCommand('insertHTML', false, '<p><br></p>');
    
    // Update parent with new content
    shouldProcessEditRef.current = false;
    onChange(editorRef.current.innerHTML);
    
    // Restore selection after the state update
    setTimeout(() => {
      restoreSelection();
    }, 0);
  };

  // Function to handle various formatting commands
  const formatText = (command: string, value?: string) => {
    if (!editorRef.current) return;
    
    // Save selection
    const savedSelection = saveSelection();
    
    // Execute command
    document.execCommand(command, false, value);
    
    // Get the updated HTML
    shouldProcessEditRef.current = false;
    onChange(editorRef.current.innerHTML);
    
    // Restore selection after the state update
    setTimeout(() => {
      restoreSelection();
    }, 0);
  };

  // Handle input with preserved selection
  const handleInput = () => {
    if (!editorRef.current) return;
    
    // Save current selection
    const savedSelection = saveSelection();
    
    // Update parent with new content
    shouldProcessEditRef.current = false;
    onChange(editorRef.current.innerHTML);
    
    // Restore selection after the state update
    setTimeout(() => {
      restoreSelection();
    }, 0);
  };

  // Helper for HTML view to handle textarea changes
  const handleHtmlChange = (newHtml: string) => {
    onChange(newHtml);
  };

  // Handle click in the editor to update selection
  const handleEditorClick = () => {
    saveSelection();
  };

  // Handle keydown to catch special keys
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveSelection();
      
      // Special handling for Shift+Enter
      if (e.shiftKey) {
        e.preventDefault();
        
        // Insert a line break instead of paragraph
        document.execCommand('insertHTML', false, '<br>');
        
        // Update parent with new content
        shouldProcessEditRef.current = false;
        onChange(editorRef.current?.innerHTML || '');
        
        // Restore selection after the state update
        setTimeout(() => {
          restoreSelection();
        }, 0);
      }
    }
  };

  // Show loading state initially
  if (!mounted) {
    return (
      <div 
        className={`border border-gray-300 rounded-md p-2 ${className}`}
        style={{ height: `${height}px` }}
      >
        <div className="animate-pulse h-full bg-gray-100 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="border border-gray-300 rounded-md overflow-hidden">
        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300 flex flex-wrap items-center">
          <div className="flex space-x-1 mr-3">
            <button 
              type="button"
              onClick={() => formatText('bold')}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button 
              type="button"
              onClick={() => formatText('italic')}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Italic"
            >
              <i>I</i>
            </button>
            <button 
              type="button"
              onClick={() => formatText('underline')}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Underline"
            >
              <u>U</u>
            </button>
          </div>
          
          <div className="flex space-x-1 mr-3">
            <button 
              type="button"
              onClick={() => formatText('insertUnorderedList')}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Bullet List"
            >
              • List
            </button>
            <button 
              type="button"
              onClick={() => formatText('insertOrderedList')}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Numbered List"
            >
              1. List
            </button>
          </div>
          
          <div className="flex space-x-1">
            <button 
              type="button"
              onClick={insertParagraph}
              className="px-2 py-1 hover:bg-gray-200 rounded"
              title="Add Paragraph"
            >
              ¶ Para
            </button>
            <button 
              type="button"
              onClick={() => setIsHtmlMode(!isHtmlMode)}
              className={`px-2 py-1 hover:bg-gray-200 rounded ${isHtmlMode ? 'bg-gray-300' : ''}`}
              title="HTML Mode"
            >
              HTML
            </button>
          </div>
        </div>
        
        {isHtmlMode ? (
          <textarea
            value={value}
            onChange={(e) => handleHtmlChange(e.target.value)}
            placeholder={placeholder}
            className="w-full border-none focus:ring-0 p-2"
            style={{ height: `${height - 36}px`, resize: 'none' }}
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning={true}
            onInput={handleInput}
            onClick={handleEditorClick}
            onKeyDown={handleKeyDown}
            className="w-full border-none focus:ring-0 p-2 outline-none"
            style={{ 
              height: `${height - 36}px`, 
              overflowY: 'auto',
              minHeight: '100px'
            }}
            dangerouslySetInnerHTML={{ __html: value }}
          />
        )}
        
        <div className="px-2 py-1 text-xs text-gray-500 border-t border-gray-200 flex justify-between">
          <span>HTML formatting is supported</span>
          <span>Press Enter for new line</span>
        </div>
      </div>
    </div>
  );
}

// Helper function to safely render HTML content
export function RichTextContent({ content }: { content: string }) {
  if (!content) return null;
  
  // Process content to ensure proper display
  const processContent = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/\n/g, '<br />') // Convert newlines to <br> tags
      .replace(/<\/p><p>/g, '</p>\n<p>'); // Add spacing between paragraphs
  };
  
  const processedContent = processContent(content);
  
  return (
    <div 
      dangerouslySetInnerHTML={{ __html: processedContent }} 
      className="rich-text-content"
    />
  );
}